// TTS Service Manager - Simplified to use only Kokoro engine
import { ITTSEngine, TTSOptions } from './tts/ITTSEngine';
import { KokoroEngine } from './tts/KokoroEngine';

export class TTSService {
  private engine: ITTSEngine;
  private enabled: boolean;

  constructor() {
    this.engine = new KokoroEngine();
    this.enabled = false;
    this.loadPreferences();

    // Initialize Kokoro engine in the background
    this.initializeEngine();
  }

  private async initializeEngine(): Promise<void> {
    console.log('TTSService: Initializing Kokoro engine...');
    try {
      await this.engine.initialize();
      console.log('TTSService: KokoroTTS ready');
    } catch (error) {
      console.error('TTSService: KokoroTTS failed:', error);
    }
  }

  async speak(text: string, options?: TTSOptions): Promise<void> {
    console.log('TTSService.speak called with:', { text: text.substring(0, 50) + '...', enabled: this.enabled });

    if (!this.enabled) {
      console.log('TTS not enabled, skipping speech');
      return;
    }

    console.log('Engine:', this.engine.name, 'Ready:', this.engine.isReady());

    // Ensure engine is ready
    if (!this.engine.isReady()) {
      console.log('Engine not ready, initializing...');
      try {
        await this.engine.initialize();
      } catch (error) {
        console.error('Engine initialization failed:', error);
        throw new Error(`TTS engine failed to initialize: ${error}`);
      }
    }

    console.log('About to call engine.speak...');
    try {
      await this.engine.speak(text, options);
      console.log('Engine.speak completed');
    } catch (error) {
      console.error('Engine.speak failed:', error);
      throw error;
    }
  }

  stop(): void {
    this.engine.stop();
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.savePreferences();

    if (!enabled) {
      this.stop();
    }
  }

  getCurrentEngineInfo(): ITTSEngine {
    return this.engine;
  }

  private loadPreferences(): void {
    try {
      const saved = localStorage.getItem('tts-preferences');
      if (saved) {
        const preferences = JSON.parse(saved);
        this.enabled = preferences.enabled || false;
      }
    } catch (error) {
      console.warn('Failed to load TTS preferences:', error);
    }
  }

  private savePreferences(): void {
    try {
      const preferences = {
        enabled: this.enabled
      };
      localStorage.setItem('tts-preferences', JSON.stringify(preferences));
    } catch (error) {
      console.warn('Failed to save TTS preferences:', error);
    }
  }

  // Singleton instance
  private static instance: TTSService | null = null;

  static getInstance(): TTSService {
    if (!TTSService.instance) {
      TTSService.instance = new TTSService();
    }
    return TTSService.instance;
  }
}
